import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import {
  useTaskChecklist,
  useAddChecklistItem,
  useUpdateChecklistItem,
  useRemoveChecklistItem,
} from '@/hooks/useTasks'
import { TaskChecklist, type ChecklistRow } from './TaskChecklist'
import { usePermissions } from '@/hooks/usePermissions'

interface Props {
  taskId: string
  /** When true, checklist is view-only (no add/toggle/remove). */
  readOnly?: boolean
  variant?: 'detail' | 'form'
}

// Detail-page checklist (feedback #69). API-backed: each add/toggle/remove
// persists immediately and refetches.
export function TaskChecklistSection({
  taskId,
  readOnly = false,
  variant = 'detail',
}: Props) {
  const { t } = useTranslation('tasks')
  const { canEdit, canDelete } = usePermissions()
  const canMutate = !readOnly && canEdit('tasks')
  const canRemove = canMutate && canDelete('tasks')
  const { data, isLoading } = useTaskChecklist(taskId)
  const add = useAddChecklistItem(taskId)
  const toggle = useUpdateChecklistItem(taskId)
  const remove = useRemoveChecklistItem(taskId)
  const [localDoneById, setLocalDoneById] = useState<Record<string, boolean>>(
    {}
  )
  const [pendingToggleById, setPendingToggleById] = useState<
    Record<string, true>
  >({})

  const items: ChecklistRow[] = useMemo(
    () =>
      (data?.data ?? []).map(i => ({
        id: i.id,
        label: i.label,
        is_done: localDoneById[i.id] ?? i.is_done,
      })),
    [data, localDoneById]
  )

  const onError = (e: unknown, fallback: string) =>
    toast({ variant: 'destructive', title: getApiErrorMessage(e, fallback) })

  const pending = add.isPending || remove.isPending

  if (isLoading) {
    return <Skeleton className='h-24 w-full' />
  }

  return (
    <TaskChecklist
      items={items}
      variant={variant}
      disabled={pending || !canMutate}
      allowRemove={canRemove}
      onAdd={label =>
        add
          .mutateAsync({ label })
          .catch(e => onError(e, t('checklist.addError')))
      }
      onToggle={(item, next) => {
        if (!item.id || !canMutate) return
        if (pendingToggleById[item.id]) return

        const itemId = item.id
        const previous = item.is_done
        setLocalDoneById(current => ({ ...current, [itemId]: next }))
        setPendingToggleById(current => ({ ...current, [itemId]: true }))

        toggle
          .mutateAsync({ itemId, body: { is_done: next } })
          .then(() => {
            setLocalDoneById(current => {
              const copy = { ...current }
              delete copy[itemId]
              return copy
            })
          })
          .catch(e => {
            setLocalDoneById(current => ({ ...current, [itemId]: previous }))
            onError(e, t('checklist.updateError'))
          })
          .finally(() => {
            setPendingToggleById(current => {
              const copy = { ...current }
              delete copy[itemId]
              return copy
            })
          })
      }}
      onRemove={item => {
        if (!item.id || !canRemove) return
        remove
          .mutateAsync(item.id)
          .catch(e => onError(e, t('checklist.removeError')))
      }}
    />
  )
}

export default TaskChecklistSection
